import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { useFormApi } from "../useForm";
import LabeledControl from "../utils/LabeledControl";
function getCanonicalValue(value, type) {
    switch (type) {
        case "integer":
            if (value === "") {
                return null;
            }
            else {
                const int = parseInt(value.replace(/ /g, ""));
                if (!/^-?[0-9]+$/.test(value) || isNaN(int)) {
                    return;
                }
                return int;
            }
        case "decimal":
            if (value === "") {
                return null;
            }
            else {
                const float = parseFloat(value === null || value === void 0 ? void 0 : value.replace(/ /g, "").replace(",", "."));
                if (!/^-?[0-9]+((,|\.)[0-9]+)?$/.test(value) || isNaN(float)) {
                    return;
                }
                return float;
            }
    }
    return value;
}
function formatValue(value, useGrouping, decimals, locale) {
    if (value === undefined || value === null)
        return "";
    return value
        .toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping,
    })
        .replace(/\s/g, " ");
}
/**
 * This is the input control for numeric `<input>` types.
 *
 * State value will be set to `number` or `null` (when empty).
 */
function NumberInput({ id, className, langKey, locale, label, defaultValue, type = "integer", info = false, autoFocus = false, placeholder, required = false, disabled = false, decimals = 2, ...inputProps }) {
    const ref = useRef(null);
    const form = useFormApi();
    const { getValue, setValue } = form;
    const [hasFocus, setFocus] = useState(false);
    const [rawValue, setRawValue] = useState(() => {
        var _a, _b;
        const v = (_b = (_a = getValue(id)) !== null && _a !== void 0 ? _a : defaultValue) !== null && _b !== void 0 ? _b : "";
        if (typeof v === "number")
            return formatValue(v, true, type === "integer" ? 0 : decimals, locale);
        return v;
    });
    if (!langKey && typeof id === "string")
        langKey = id;
    useEffect(() => {
        var _a, _b;
        if (autoFocus) {
            (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.getElementsByTagName("input")[0]) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }, [autoFocus]);
    const handleFocus = useCallback(() => {
        const value = getValue(id);
        setFocus(true);
        setRawValue(formatValue(value, false, type === "integer" ? 0 : decimals, locale));
    }, [type, decimals, locale]);
    const handleBlur = useCallback(() => setFocus(false), []);
    const value = getValue(id);
    useEffect(() => {
        if (hasFocus)
            return;
        setRawValue(formatValue(value, true, type === "integer" ? 0 : decimals, locale));
    }, [hasFocus, value, type, decimals, locale]);
    const handleChange = useCallback((e) => {
        setRawValue(e.target.value);
        const value = getCanonicalValue(e.target.value, type);
        if (getValue(id) === value || value === undefined) {
            return;
        }
        setValue(id, value);
    }, [id, type, getValue, setValue]);
    const inputPlaceholder = useMemo(() => {
        if (placeholder || placeholder === null || placeholder === "")
            return placeholder;
        switch (type) {
            case "integer":
                return "0";
            case "decimal":
                return (0).toLocaleString(undefined, {
                    minimumFractionDigits: decimals,
                });
        }
    }, [type, decimals, placeholder]);
    return (React.createElement(LabeledControl, { id: id, langKey: langKey, label: label, info: info, required: required, disabled: disabled, ref: ref, className: `aui-${type} ${className !== null && className !== void 0 ? className : ""}` },
        React.createElement("input", { id: id, type: "text", ...inputProps, value: rawValue, onChange: handleChange, onFocus: handleFocus, onBlur: handleBlur, placeholder: inputPlaceholder || " ", autoComplete: "off", required: required, pattern: type === "integer"
                ? hasFocus
                    ? "-?[0-9 ]+"
                    : "-?[0-9 ,]+"
                : hasFocus
                    ? "-?[0-9 ]*((\\.|,)?[0-9 ]+)?"
                    : "-?[0-9 ,]*((\\.|,)?[0-9 ,]+)?", disabled: disabled, inputMode: type === "integer" ? "numeric" : "decimal" })));
}
export default NumberInput;
